import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Textarea,
  VStack,
  useToast,
  Checkbox,
} from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useForm } from 'react-hook-form';
import { PrePlacementNominationReviewStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/PrePlacementNominationReviewStatus.schema';
import { trpcAdmin } from '../../lib/trpcAdmin';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';

interface RejectNominationModalProps {
  isOpen: boolean;
  onClose: () => void;
  nominationId: string;
  onSuccess: () => void;
}

interface FormData {
  reviewNotes: string;
  requestReplacement: boolean;
}

export function RejectNominationModal({
  isOpen,
  onClose,
  nominationId,
  onSuccess,
}: RejectNominationModalProps): React.JSX.Element {
  const toast = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: { reviewNotes: '', requestReplacement: false } });

  const rejectMutation = trpcAdmin.workPlacements.placements.reviewNomination.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Nomination rejected` });
      reset();
      onSuccess();
    },
    onError: (error) => {
      toast({ status: 'error', title: t`Failed to reject nomination`, description: error.message });
    },
  });

  const onSubmit = (data: FormData) => {
    rejectMutation.mutate({
      nominationId,
      action: PrePlacementNominationReviewStatusSchema.enum.REJECTED,
      reviewNotes: data.reviewNotes,
      requestReplacement: data.requestReplacement,
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t`Reject nomination`}
      body={
        <VStack align="stretch" spacing={4}>
          <FormControl isRequired isInvalid={!!errors.reviewNotes}>
            <FormLabel>
              <Trans>Reason for rejection</Trans>
            </FormLabel>
            <Textarea
              {...register('reviewNotes', {
                required: t`Please provide a reason for rejection`,
              })}
              placeholder={t`Explain why this nomination is being rejected`}
              rows={3}
            />
            {errors.reviewNotes ? (
              <FormErrorMessage>{errors.reviewNotes.message}</FormErrorMessage>
            ) : null}
          </FormControl>
          <Checkbox {...register('requestReplacement')}>
            <Trans>Request a replacement nomination</Trans>
          </Checkbox>
        </VStack>
      }
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel={t`Reject`}
      isLoading={rejectMutation.isPending}
      variant="destructive"
    />
  );
}
